import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectIsDirty } from "../store/ccOnboardingSlice";

const DEFAULT_MESSAGE =
  "You have unsaved changes in the CC Onboarding form. If you leave now, this step's edits will be lost.";

/**
 * Blocks the two kinds of navigation React Router cannot see:
 *   1. Tab close / refresh / typed URL / bookmark  -> beforeunload
 *   2. Browser Back / Forward buttons               -> popstate
 *
 * Reads `isDirty` straight from the ccOnboarding redux slice, so any
 * component dispatching `markDirty()` (every form-field edit) or
 * `clearDirty()` (a successful step save / submit) automatically
 * arms or disarms the guard — nothing to plumb through props.
 *
 * This app uses a plain <BrowserRouter>/<Routes> tree, not a data
 * router, so React Router's own useBlocker() isn't available here;
 * this hook is the equivalent for that setup.
 */
export default function useNavigationGuard(message = DEFAULT_MESSAGE) {
  const isDirty = useSelector(selectIsDirty);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // 1. Tab close / refresh / typed navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = message; // Chrome requires returnValue to be set
      return message;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [message]);

  // 2. Browser Back / Forward
  useEffect(() => {
    if (!isDirty) return undefined;

    // Sentinel entry: the first Back press lands here and fires
    // popstate without actually leaving the page, giving us a chance
    // to confirm before letting the user go further.
    window.history.pushState({ __ccOnboardingGuard: true }, "");

    const handlePopState = () => {
      if (!isDirtyRef.current) return;
      const leave = window.confirm(message);
      if (leave) {
        window.removeEventListener("popstate", handlePopState);
        window.history.go(-1);
      } else {
        window.history.pushState({ __ccOnboardingGuard: true }, "");
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line
  }, [isDirty, message]);
}

/**
 * For in-app navigation the guard controls directly — the wizard's own
 * "Exit" link, a sidebar item, a tab switch — use this instead of a
 * bare useNavigate(). It shows a proper in-app modal (render
 * <ConfirmLeaveModal {...modalProps} /> once, near the root of the
 * page) rather than a browser confirm() popup.
 */
export function useConfirmedNavigate() {
  const navigate = useNavigate();
  const isDirty = useSelector(selectIsDirty);
  const [pendingTo, setPendingTo] = useState(null);

  const navigateSafely = useCallback(
    (to, options) => {
      if (isDirty) {
        setPendingTo({ to, options });
      } else {
        navigate(to, options);
      }
    },
    [isDirty, navigate]
  );

  const confirmLeave = useCallback(() => {
    if (pendingTo) navigate(pendingTo.to, pendingTo.options);
    setPendingTo(null);
  }, [navigate, pendingTo]);

  const cancelLeave = useCallback(() => setPendingTo(null), []);

  return {
    navigateSafely,
    isPromptOpen: !!pendingTo,
    confirmLeave,
    cancelLeave,
  };
}
